import React, { useCallback, useMemo, useRef } from 'react';
import { Button, CircularProgress, DialogContentText } from '@mui/material';
import { Add as AddIcon, } from '@mui/icons-material';
import noop from 'lodash/noop';
import { uniqueId } from 'lodash';
import useDialog from '@dyl/hooks/useDialog';
import stubNull from '@dyl/utils/stubNull';

/**
 * **NB: This component contains no functionality for sending the file anywhere.
 *  It serves only as a device to display a button, confirmation prompt,
 *  and loading screen.**
 * @param {Object} props
 * @param {string} [props.label]
 * @param {(file: File) => Promise<void>} [props.onConfirm]
 * @param {string} [accept]
 * @param {(file: File) => JSX.Element} [props.Preview]
 */
export default function Uploader(props) {
    const {
        label = 'Upload',
        onConfirm = noop,
        accept,
        Preview = stubNull,
        ...other
    } = props;

    const [ImperativeDialog, openDialog, closeDialog] = useDialog();

    const inputId = useMemo(() => uniqueId('object-picker-uploader-input-'), []);

    /** @type {React.MutableRefObject<HTMLInputElement>} */
    const inputRef = useRef();

    const onFileSelect = useCallback(async() => {
        const file = inputRef.current.files?.[0];

        /* Clear input element, so that the same file can be selected again.
         *  Without this, the change event is not invoked if the same
         *  file is selected. */
        inputRef.current.value = '';

        if (!file) return;

        const didConfirm = await new Promise(resolve => openDialog(
            'Confirm upload', (
                <>
                    <DialogContentText>{file.name}</DialogContentText>
                    <Preview file={file} />
                    <DialogContentText>Upload this file?</DialogContentText>
                </>
            ), [
                { text: 'Cancel', onClick: () => resolve(false) },
                { text: 'Upload', onClick: () => resolve(true) },
            ],
            { contentProps: { style: { textAlign: 'center' }}},
        ));

        if (didConfirm) {
            openDialog(
                `Uploading ${file.name}...`,
                <CircularProgress size='3em' />,
                [],
                { contentProps: { style: { textAlign: 'center' }}},
            );

            try {
                await onConfirm(file);
            }
            catch (error) {
                console.error(error);
            }
        }

        closeDialog();
    }, [onConfirm, Preview]);

    return (
        <>
            <Button
                color='primary'
                component='label'
                htmlFor={inputId}
                {...other}
            >
                <AddIcon/> {label}
                <input
                    type='file'
                    ref={inputRef}
                    id={inputId}
                    style={{ display: 'none' }}
                    accept={accept}
                    onChange={onFileSelect}
                />
            </Button>

            <ImperativeDialog
                fullWidth
                maxWidth='sm'
            />
        </>
    );
}